import React, { useMemo } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import MarkdownPreviewEditor from '../MarkdownPreviewEditor';
import VariablePromptEditor from './VariablePromptEditor';
import { instructionBodyTextSx, instructionPreviewMarkdownSx } from '../markdownBodySx';
import { mergeVariablesFromTemplate } from '../../../utils/promptVariables';

/**
 * Shared AI instruction body editor/preview used in PromptDialog and BuildTab.
 * Merges variables from template text so {{tokens}} render even when metadata is incomplete.
 */
export default function CapabilityInstructionBody({
  value = '',
  onChange,
  variables = [],
  variableValues = {},
  mode,
  onModeChange,
  onVariableClick,
  minRows = 10,
  placeholder,
  editorControlRef,
  onBeforePreview,
  onBlur,
  keepEditorMounted = false,
  clickAwayToPreview = false,
  showToggle = false,
  embedded = false,
  contentTextSx = instructionBodyTextSx,
  previewSx = { '& .markdown-body': instructionPreviewMarkdownSx },
  lockedSourceExtension = '',
  lockedMessage = '',
}) {
  const effectiveVariables = useMemo(
    () => mergeVariablesFromTemplate(value, variables),
    [value, variables]
  );

  const isLocked = Boolean(lockedSourceExtension);

  return (
    <MarkdownPreviewEditor
      value={value}
      onChange={onChange}
      mode={mode}
      onModeChange={onModeChange}
      minRows={minRows}
      showToggle={showToggle}
      clickAwayToPreview={clickAwayToPreview}
      onBeforePreview={onBeforePreview}
      keepEditorMounted={keepEditorMounted}
      editorControlRef={editorControlRef}
      contentTextSx={contentTextSx}
      placeholder={placeholder}
      variables={effectiveVariables}
      variableValues={variableValues}
      onVariableClick={onVariableClick}
      previewSx={previewSx}
      renderEditor={() => (
        isLocked ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              {lockedMessage || `Editing is locked for this imported ${lockedSourceExtension.toUpperCase()} file because the prompt editor only supports markdown/text editing safely.`}
            </Alert>
            <Typography
              component="pre"
              variant="body2"
              sx={{
                m: 0,
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                color: 'text.secondary',
              }}
            >
              {value}
            </Typography>
          </Box>
        ) : (
          <VariablePromptEditor
            ref={editorControlRef}
            embedded={embedded}
            value={value}
            onChange={onChange}
            variables={effectiveVariables}
            variableValues={variableValues}
            onVariableClick={onVariableClick}
            onBlur={onBlur}
            minRows={minRows}
            placeholder={placeholder}
          />
        )
      )}
    />
  );
}
